import { i18n } from '../i18n.js';
import { warnBeforeEnabling } from './duel-cost-warning.js';
import { answerLockedTap, markSwitchLocked } from './locked-switch.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';

let LucidGamePreference = {};

LucidGamePreference = (function () {
    function uiText(key, fallback) {
        const translated = i18n?.t?.(key);
        return translated && translated !== key ? translated : fallback;
    }

    /* LUCID IS THE SIBLING OF THE MANGA SWITCH, WITH THE OPPOSITE DEFAULT.
       Manga starts off every battle by the owner's explicit instruction. Lucid
       starts ON for subscribers, because a sharper storyteller is the thing the
       subscription is FOR — a player who pays for it and then has to switch it
       on every duel would reasonably say they were sold something and not given
       it. Free has no on state at all: the light profile IS the free tier.

       The switch is per-battle and writes nothing to storage, exactly like the
       manga one. `resolveGameTextProfile` on the server is the authority; this
       control cannot grant anything, only request. */

    function mount(slot, status) {
        if (!slot) throw new Error('Lucid preference slot is required');
        slot.replaceChildren();

        /* Same three states as the manga control, and the same rule that an
           absent status is NOT a verdict: a failed entitlement read must never
           tell a paying subscriber they need to buy what they already own. */
        const known = status !== null && status !== undefined
            && (status.eligible === true || status.eligible === false);
        const usable = status?.eligible === true;
        /* A verdict, not a gap: the plan is known and does not include it. */
        const refused = known && !usable;

        let enabled = usable;                       // DEFAULT ON for subscribers

        const root = document.createElement('div');
        root.className = 'lucid-game-preference';
        if (!usable) root.classList.add('is-disabled');
        if (!known) root.classList.add('is-unknown');

        const description = document.createElement('span');
        description.id = 'lucid-game-preference-description';
        description.className = 'lucid-game-preference__description';
        if (usable) {
            description.textContent = uiText(
                'lucid.preference.description',
                'A sharper storyteller.',
            );
        } else if (!known) {
            description.textContent = uiText(
                'lucid.preference.statusUnavailable',
                'Checking your plan…',
            );
        } else {
            /* THE FREE-PLAYER LINE, REWRITTEN. The manga control told a free
               player "Manga Pass inks your duel into manga" — a sentence that
               assumes they already know what Manga Pass is, never says it costs
               money, and reads like a switch that is simply broken. This says
               what the feature does, that it is paid, and that the switch is
               therefore off on purpose. */
            description.textContent = uiText(
                'lucid.preference.paidOnly',
                'A sharper storyteller. Included with any paid plan.',
            );
        }

        const label = document.createElement('span');
        label.className = 'lucid-game-preference__label';
        label.textContent = uiText('lucid.preference.label', 'Pro Storyteller');

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'lucid-game-preference__switch';
        toggle.setAttribute('role', 'switch');
        toggle.setAttribute('aria-describedby', description.id);
        toggle.setAttribute('aria-label', label.textContent);
        if (!usable) toggle.setAttribute('aria-disabled', 'true');

        const state = document.createElement('span');
        state.className = 'lucid-game-preference__state';
        const compactLabel = document.createElement('span');
        compactLabel.className = 'lucid-game-preference__compact-label';
        compactLabel.textContent = uiText('lucid.preference.compactLabel', 'Pro Storyteller');
        const track = document.createElement('span');
        track.className = 'lucid-game-preference__track';
        track.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('span');
        thumb.className = 'lucid-game-preference__thumb';
        track.appendChild(thumb);
        if (refused) markSwitchLocked(root, thumb);

        function render() {
            toggle.setAttribute('aria-checked', String(enabled));
            state.textContent = enabled
                ? uiText('lucid.preference.on', 'On')
                : uiText('lucid.preference.off', 'Off');
        }

        toggle.addEventListener('click', async () => {
            if (!usable) {
                /* Inert is not silent. The tap is acknowledged and answered
                   with the same sentence the description carries — on a phone
                   that sentence is otherwise never on screen. */
                answerLockedTap(root, toggle, { message: description.textContent, refused });
                MoonfallAnalytics?.capture?.('lucid_game_preference_locked_tap', {
                    reason: refused ? 'subscription_required' : 'status_unknown',
                });
                return;
            }
            if (!enabled && !(await warnBeforeEnabling('lucid'))) return;
            enabled = !enabled;
            render();
            MoonfallAnalytics?.capture?.('lucid_game_preference_changed', { enabled });
        });

        toggle.append(compactLabel, state, track);
        root.append(label, toggle, description);
        slot.appendChild(root);
        render();

        return Object.freeze({
            /* UNDEFINED, not false, when the player had no choice to make.
               `deck-selector.js` only forwards a boolean, so an ineligible or
               still-loading control sends no `lucid` key at all — and the
               server reads an absent flag as the tier's own default, which for
               a subscriber is Lucid ON and for a free account is the light
               profile. Returning `false` here would instead assert "the player
               turned it off", changing the payload shape for everyone whose
               control never became usable. */
            value: () => (usable ? enabled : undefined),
            destroy: () => root.remove(),
        });
    }

    return { mount, warnBeforeEnabling };
})();

export { LucidGamePreference };
